import { Vector3 } from 'three';
import { Halfedge } from '../core/Halfedge';
import { HalfedgeDS } from '../core/HalfedgeDS';
import { Vertex } from '../core/Vertex';
import { Face } from '../core/Face';
import { FaceMapTxn } from '../utils/FaceMapTxn';

type CutResult = {
  vertex: Vertex;
  leftFaces?: { kept: Face; created?: Face };
  rightFaces?: { kept: Face; created?: Face };
};

/**
 * Monolithic variant of cutEdge:
 *  - inserts V on edge he (A->B),
 *  - reconnects halfedges on both sides,
 *  - retriangulates locally (inner: 2→4, boundary: 1→2),
 *  - incrementally updates verticesToFace using FaceMapTxn.
 */
export function cutEdge(struct: HalfedgeDS, he: Halfedge, position: Vector3, tolerance = 1e-10): CutResult {
  const twin = he.twin;
  const A = he.vertex;
  const B = twin.vertex;

  // Snap to ends – no change
  if (A.matchesPosition(position, tolerance)) return { vertex: A };
  if (B.matchesPosition(position, tolerance)) return { vertex: B };

  // Incident faces
  const leftFace = he.face; // ABC
  const he_BC = he.next; // B->C
  const he_CA = he.prev; // C->A
  const C = leftFace ? he_BC.next.vertex : null;

  const rightFace = twin.face; // BAD
  const he_AD = twin.next; // A->D
  const he_DB = twin.prev; // D->B
  const D = rightFace ? he_AD.next.vertex : null;

  // TXN and verticesToFace: first mark deletion of original faces
  const txn = new FaceMapTxn(struct);
  if (leftFace) txn.deleteFace(leftFace);
  if (rightFace) txn.deleteFace(rightFace);

  // --- create new vertex V ---
  const V = struct.addVertex(position, struct.vertices.length, he.vertex.color);

  // --- split edge and reconnect loops ---
  // Recycle he as A->V, add V->B / B->V / V->A
  const he_AV = he; // A->V (recycled)
  const he_VB = new Halfedge(V); // V->B
  const he_BV = new Halfedge(B); // B->V
  const he_VA = new Halfedge(V); // V->A

  // Twin connections
  he_AV.twin = he_VA;
  he_VA.twin = he_AV;
  he_VB.twin = he_BV;
  he_BV.twin = he_VB;

  // Face connections (currently copies of original faces)
  he_AV.face = leftFace;
  he_VB.face = leftFace;
  he_BV.face = rightFace;
  he_VA.face = rightFace;

  // Left loop (A->B->C->A) → (A->V->B->C->A)
  const left_next_old = he_BC;
  he_AV.next = he_VB;
  he_VB.prev = he_AV; // A->V -> V->B
  he_VB.next = left_next_old;
  left_next_old.prev = he_VB; // V->B -> B->C

  // Right loop (D->B->A->D) → (D->B->V->A->D)
  const right_prev_old = twin.prev; // D->B

  // Ordering: D->B -> B->V -> V->A -> A->B
  right_prev_old.next = he_BV;
  he_BV.prev = right_prev_old;
  he_BV.next = he_VA;
  he_VA.prev = he_BV;
  he_VA.next = twin;
  twin.prev = he_VA;

  // References to vertices
  V.halfedge = he_VB;
  B.halfedge = he_BV;

  // Register new objects
  struct.halfedges.push(he_VB, he_BV, he_VA);

  const result: CutResult = { vertex: V };

  // --- local triangulation left side ---
  if (leftFace && C) {
    // Create diagonal V<->C
    const he_VC = new Halfedge(V);
    const he_CV = new Halfedge(C);
    he_VC.twin = he_CV;
    he_CV.twin = he_VC;

    // Keep face references
    he_AV.face = leftFace;
    he_VC.face = leftFace;
    he_CA.face = leftFace;

    he_AV.next = he_VC;
    he_VC.prev = he_AV;
    he_VC.next = he_CA;
    he_CA.prev = he_VC;
    he_CA.next = he_AV;
    he_AV.prev = he_CA;
    leftFace.halfedge = he_AV;

    // New face left = (V,B,C)
    const newLeftFace = new Face(he_VB);
    he_VB.face = newLeftFace;
    he_BC.face = newLeftFace;
    he_CV.face = newLeftFace;

    he_VB.next = he_BC;
    he_BC.prev = he_VB;
    he_BC.next = he_CV;
    he_CV.prev = he_BC;
    he_CV.next = he_VB;
    he_VB.prev = he_CV;

    struct.halfedges.push(he_VC, he_CV);
    struct.faces.push(newLeftFace);

    // Maps
    txn.putTriple(A.id, V.id, C.id, leftFace);
    txn.putTriple(V.id, B.id, C.id, newLeftFace);

    result.leftFaces = { kept: leftFace, created: newLeftFace };
  }

  // --- local triangulation right side ---
  if (rightFace && D) {
    // Create diagonal V<->D
    const he_VD = new Halfedge(V);
    const he_DV = new Halfedge(D);
    he_VD.twin = he_DV;
    he_DV.twin = he_VD;

    // Keep face references
    he_VA.face = rightFace;
    he_AD.face = rightFace;
    he_DV.face = rightFace;

    he_VA.next = he_AD;
    he_AD.prev = he_VA;
    he_AD.next = he_DV;
    he_DV.prev = he_AD;
    he_DV.next = he_VA;
    he_VA.prev = he_DV;
    rightFace.halfedge = he_VA;

    // New face right = (B,V,D)
    const newRightFace = new Face(he_BV);
    he_BV.face = newRightFace;
    he_VD.face = newRightFace;
    he_DB.face = newRightFace;

    he_BV.next = he_VD;
    he_VD.prev = he_BV;
    he_VD.next = he_DB;
    he_DB.prev = he_VD;
    he_DB.next = he_BV;
    he_BV.prev = he_DB;

    struct.halfedges.push(he_VD, he_DV);
    struct.faces.push(newRightFace);

    // Maps
    txn.putTriple(V.id, A.id, D.id, rightFace);
    txn.putTriple(B.id, V.id, D.id, newRightFace);

    result.rightFaces = { kept: rightFace, created: newRightFace };
  }

  // Commit changes to the map
  txn.commit();

  return result;
}
